import {
  AloftArea,
  AloftAreaJSON,
  DEFAULT_ATTIC_SIZE_SQFT,
  DEFAULT_BARN_SIZE_SQFT,
  DEFAULT_CARPORT_SIZE_SQFT,
  DEFAULT_DECK_SIZE_SQFT,
  DEFAULT_GARAGE_SIZE_SQFT,
  DEFAULT_PATIO_SIZE_SQFT,
  DEFAULT_POOL_AREA_SIZE_SQFT,
  DEFAULT_PORCH_SIZE_SQFT,
  DEFAULT_SHED_SIZE_SQFT,
  DEFAULT_SPA_SIZE_SQFT,
  DEFAULT_WORKSHOP_SIZE_SQFT,
  FeatureAreaKey,
  Improvements,
  ImprovementsSelectedSources,
  SourceType,
  Tkv2PublicRecordJSON,
  featureAreaKeys,
  schAloftArea,
  schSourceType,
} from '@aloft/toolkit-shared';

import { PropertyGridColumnDefinition } from '../types.ts';

const isFeatureAreaKey = (feature: string): feature is FeatureAreaKey =>
  (featureAreaKeys as readonly string[]).includes(feature);

const featureAreaFieldPattern = /improvements\.(\w+)\.area$/;

const PREFERRED_AREA_SOURCES: SourceType[] = [
  schSourceType.enum.abode_mls,
  schSourceType.enum.abode_public_record,
  schSourceType.enum.inspectify_inspection,
];

const HARDCODED_DEFAULT_AREA_SQFT: Partial<Record<FeatureAreaKey, number>> = {
  garage: DEFAULT_GARAGE_SIZE_SQFT.toSqFt().unitAmount,
  detachedGarage: DEFAULT_GARAGE_SIZE_SQFT.toSqFt().unitAmount,
  carport: DEFAULT_CARPORT_SIZE_SQFT.toSqFt().unitAmount,
  attic: DEFAULT_ATTIC_SIZE_SQFT.toSqFt().unitAmount,
  barn: DEFAULT_BARN_SIZE_SQFT.toSqFt().unitAmount,
  deck: DEFAULT_DECK_SIZE_SQFT.toSqFt().unitAmount,
  patio: DEFAULT_PATIO_SIZE_SQFT.toSqFt().unitAmount,
  porch: DEFAULT_PORCH_SIZE_SQFT.toSqFt().unitAmount,
  pool: DEFAULT_POOL_AREA_SIZE_SQFT.toSqFt().unitAmount,
  shed: DEFAULT_SHED_SIZE_SQFT.toSqFt().unitAmount,
  spa: DEFAULT_SPA_SIZE_SQFT.toSqFt().unitAmount,
  workshop: DEFAULT_WORKSHOP_SIZE_SQFT.toSqFt().unitAmount,
};

const PUBLIC_RECORD_AREA_FIELD: Partial<Record<FeatureAreaKey, keyof Tkv2PublicRecordJSON>> = {
  garage: 'ParkingGarageArea',
  detachedGarage: 'ParkingGarageArea',
  carport: 'ParkingCarportArea',
  patio: 'PatioArea',
  deck: 'DeckArea',
  porch: 'PorchArea',
  pool: 'PoolArea',
  attic: 'RoomsAtticArea',
  shed: 'ShedArea',
};

export type SourceFeatureArea = {
  source: SourceType;
  area?: unknown;
};

const parseArea = (value: unknown): AloftAreaJSON | undefined => {
  const area = schAloftArea.safeParse(value);
  return area.success ? area.data.toJSON() : undefined;
};

const areaSqft = (value: unknown): number | undefined => {
  const area = schAloftArea.safeParse(value);
  return area.success ? area.data.toSqFt().unitAmount : undefined;
};

const isHardcodedDefaultArea = (feature: FeatureAreaKey, value: unknown): boolean => {
  const sqft = areaSqft(value);
  return sqft != null && sqft === HARDCODED_DEFAULT_AREA_SQFT[feature];
};

export const pickPreferredSourceArea = (
  sourceAreas: SourceFeatureArea[],
): { source: SourceType; area: AloftAreaJSON } | undefined => {
  for (const source of PREFERRED_AREA_SOURCES) {
    const match = sourceAreas.find((row) => row.source === source);
    const area = parseArea(match?.area);
    if (area) return { source, area };
  }
  return undefined;
};

export const getPublicRecordFeatureArea = (
  publicRecord: Tkv2PublicRecordJSON | undefined,
  feature: FeatureAreaKey,
): unknown => {
  const field = PUBLIC_RECORD_AREA_FIELD[feature];
  if (!field || !publicRecord) return undefined;
  return publicRecord[field];
};

export const getGridSourceFeatureAreas = (
  columns: PropertyGridColumnDefinition[],
  propertyId: string,
  feature: string,
): SourceFeatureArea[] => {
  if (!isFeatureAreaKey(feature)) return [];
  return columns
    .filter(
      (column) =>
        column.propertyId === propertyId &&
        PREFERRED_AREA_SOURCES.includes(column.type as SourceType),
    )
    .map((column) => ({
      source: column.type as SourceType,
      area: column.propertyData?.editableData?.improvements?.[feature]?.area,
    }));
};

const isFallbackAreaSource = (source?: SourceType) =>
  !source || source === schSourceType.enum.estimated || source === schSourceType.enum.none;

export const getDefaultFeatureAreaSqft = (
  fieldName: string,
  defaultAreas?: Partial<Record<FeatureAreaKey, number>>,
): number | undefined => {
  const feature = featureAreaFieldPattern.exec(fieldName)?.[1];
  if (!feature || !isFeatureAreaKey(feature)) return undefined;
  const defaultSqft = defaultAreas?.[feature];
  if (defaultSqft == null || defaultSqft === 0) return undefined;
  return defaultSqft;
};

export const getEstimatedFeatureAreaSourceOption = (
  fieldName: string,
  defaultAreas?: Partial<Record<FeatureAreaKey, number>>,
  toDisplayValue?: (value: string | undefined, propertyId: string) => string | undefined,
  propertyId = '',
):
  | { value?: string; rawValue: unknown; source: typeof schSourceType.enum.estimated }
  | undefined => {
  const defaultSqft = getDefaultFeatureAreaSqft(fieldName, defaultAreas);
  if (!defaultSqft) return undefined;
  const area = AloftArea.fromNumber(defaultSqft, 'sqft');
  const rawValue = area.toJSON();
  return {
    rawValue,
    value: toDisplayValue
      ? toDisplayValue(rawValue as unknown as string, propertyId)
      : area.getSiteAreaString(),
    source: schSourceType.enum.estimated,
  };
};

export const pickBestFeatureArea = ({
  feature,
  sourceAreas = [],
  defaultAreas,
}: {
  feature: FeatureAreaKey;
  sourceAreas?: SourceFeatureArea[];
  defaultAreas?: Partial<Record<FeatureAreaKey, number>>;
}): { source: SourceType; area: AloftAreaJSON } | undefined => {
  const defaultSqft = defaultAreas?.[feature];
  const preferred = pickPreferredSourceArea(sourceAreas);
  const preferredIsHardcodedDefault =
    preferred != null && isHardcodedDefaultArea(feature, preferred.area);
  if (
    preferred &&
    (!(defaultSqft && preferredIsHardcodedDefault) || defaultSqft === areaSqft(preferred.area))
  ) {
    return preferred;
  }
  if (!defaultSqft) return undefined;
  return {
    source: schSourceType.enum.estimated,
    area: AloftArea.fromNumber(defaultSqft, 'sqft').toJSON(),
  };
};

export const applyEstimatedFeatureArea = ({
  improvements,
  selectedSources,
  feature,
  defaultAreas,
  sourceAreas = [],
}: {
  improvements: Improvements;
  selectedSources?: ImprovementsSelectedSources;
  feature: string;
  defaultAreas?: Partial<Record<FeatureAreaKey, number>>;
  sourceAreas?: SourceFeatureArea[];
}): { improvements: Improvements; selectedSources?: ImprovementsSelectedSources } => {
  if (!isFeatureAreaKey(feature)) {
    return { improvements, selectedSources };
  }

  const existing = improvements[feature];
  if (!existing?.hasFeature) {
    return { improvements, selectedSources };
  }

  const existingSource = selectedSources?.[feature]?.area?.source;
  const existingIsHardcodedDefault = isHardcodedDefaultArea(feature, existing.area);
  const canReplaceExisting =
    !existing.area || isFallbackAreaSource(existingSource) || existingIsHardcodedDefault;

  if (existing.area && !canReplaceExisting) {
    return { improvements, selectedSources };
  }

  const best = pickBestFeatureArea({ feature, sourceAreas, defaultAreas });
  if (!best) {
    return { improvements, selectedSources };
  }

  const existingSqft = areaSqft(existing.area);
  const bestSqft = areaSqft(best.area);
  if (existingSqft === bestSqft && existingSource === best.source) {
    return { improvements, selectedSources };
  }

  return {
    improvements: {
      ...improvements,
      [feature]: {
        ...existing,
        area: best.area,
      },
    },
    selectedSources: {
      ...selectedSources,
      [feature]: {
        ...selectedSources?.[feature],
        area: { source: best.source },
      },
    },
  };
};
